import { useCallback, useMemo, useState } from "react";
import {
    convert, DIR_TO_IST, DIR_FROM_IST, IST_TZ, getZoneOptions, todayInZone, zoneLabel,
} from "../../lib/timeConverter";

const DEFAULT_TIME = "10:00";

/** Option value for a tz (prefer the curated city entry). */
export function optionForTz(tz) {
    if (!tz) return "";
    return getZoneOptions().find((o) => o.tz === tz)?.value || "";
}

const tzOf = (zone) => getZoneOptions().find((o) => o.value === zone)?.tz || "";

function initialState(initial, defaultTz) {
    const dir = initial?.dir || DIR_TO_IST;
    const zone = optionForTz(initial?.tz || defaultTz);
    const srcTz = dir === DIR_FROM_IST ? IST_TZ : (tzOf(zone) || IST_TZ);
    return { dir, zone, date: initial?.date || todayInZone(srcTz), time: initial?.time || DEFAULT_TIME, pick: null };
}

export function useConverterState({ initial, defaultTz = "" } = {}) {
    const [s, setS] = useState(() => initialState(initial, defaultTz));
    const [hour12, setHour12] = useState(() => initial?.fmt !== "24");
    const tz = tzOf(s.zone);
    const set = useCallback((patch) => setS((prev) => ({ ...prev, pick: null, ...patch })), []);

    const result = useMemo(() => {
        if (!tz) return { status: "no-zone" };
        return convert({ dir: s.dir, tz, date: s.date, time: s.time, pick: s.pick, hour12 });
    }, [tz, s.dir, s.date, s.time, s.pick, hour12]);

    const place = tz ? zoneLabel(tz, s.zone) : "";

    // Swap keeps the same moment: the converted value becomes the new source.
    const swap = useCallback(() => {
        const next = s.dir === DIR_TO_IST ? DIR_FROM_IST : DIR_TO_IST;
        if (result.status === "ok") {
            const d = convert({ dir: s.dir, tz, date: s.date, time: s.time, pick: s.pick, hour12: false }).dest;
            set({ dir: next, date: d.ymd, time: d.time });
        } else {
            set({ dir: next });
        }
    }, [s, tz, result.status, set]);

    const reset = useCallback(() => setS(initialState(null, defaultTz)), [defaultTz]);
    const load = useCallback((v) => setS(initialState(v, "")), []);

    return { ...s, tz, place, hour12, setHour12, set, result, swap, reset, load };
}
